import React, { useEffect, useState } from 'react';
import {
  getPrizeTemplates,
  createPrizeTemplate,
  updatePrizeTemplate,
  deletePrizeTemplate,
} from '../api/adminApi';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import Modal from '../components/ui/Modal';
import Input from '../components/ui/Input';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { Plus, Edit2, Trash2, Save, X } from 'lucide-react';

const styles = {
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 'var(--space-xl)',
    flexWrap: 'wrap',
    gap: 'var(--space-md)',
  },
  title: {
    fontFamily: 'var(--font-display)',
    fontSize: '1.75rem',
    fontWeight: 700,
    color: 'var(--color-text-primary)',
    margin: 0,
    letterSpacing: '0.03em',
  },
  subtitle: {
    fontSize: '0.9rem',
    color: 'var(--color-text-muted)',
    margin: '4px 0 0',
  },
  tableWrapper: {
    overflowX: 'auto',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid rgba(212, 175, 55, 0.1)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontFamily: 'var(--font-body)',
    fontSize: '0.9rem',
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontFamily: 'var(--font-display)',
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'var(--color-text-muted)',
    backgroundColor: 'var(--color-bg-secondary)',
    borderBottom: '1px solid rgba(212, 175, 55, 0.1)',
  },
  td: {
    padding: '12px 16px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.03)',
    color: 'var(--color-text-secondary)',
  },
  row: {
    transition: 'background-color var(--transition-fast)',
  },
  colorSwatch: {
    width: '28px',
    height: '28px',
    borderRadius: 'var(--radius-sm)',
    border: '2px solid rgba(255, 255, 255, 0.1)',
    display: 'inline-block',
    verticalAlign: 'middle',
  },
  empty: {
    textAlign: 'center',
    padding: 'var(--space-2xl)',
    color: 'var(--color-text-muted)',
  },
  centered: {
    display: 'flex',
    justifyContent: 'center',
    padding: 'var(--space-2xl)',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-md)',
  },
  colorField: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-md)',
  },
  colorInput: {
    width: '48px',
    height: '48px',
    padding: 0,
    border: 'none',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    backgroundColor: 'transparent',
  },
  colorPreview: {
    width: '36px',
    height: '36px',
    borderRadius: 'var(--radius-sm)',
    border: '2px solid rgba(212, 175, 55, 0.2)',
    flexShrink: 0,
  },
  fieldLabel: {
    fontFamily: 'var(--font-display)',
    fontSize: '0.85rem',
    fontWeight: 500,
    color: 'var(--color-text-secondary)',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    marginBottom: 'var(--space-xs)',
    display: 'block',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-sm)',
    cursor: 'pointer',
    fontSize: '0.9rem',
    color: 'var(--color-text-secondary)',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    accentColor: 'var(--color-primary)',
    cursor: 'pointer',
  },
  formActions: {
    display: 'flex',
    gap: 'var(--space-sm)',
    justifyContent: 'flex-end',
    marginTop: 'var(--space-sm)',
  },
  error: {
    padding: 'var(--space-sm) var(--space-md)',
    backgroundColor: 'rgba(231, 76, 60, 0.1)',
    border: '1px solid rgba(231, 76, 60, 0.2)',
    borderRadius: 'var(--radius-md)',
    color: 'var(--color-danger)',
    fontSize: '0.85rem',
    marginBottom: 'var(--space-md)',
  },
};

const emptyForm = {
  name: '',
  value: '',
  color: '#D4AF37',
  display_order: '0',
  is_active: true,
  prize_type: 'money',
};

function PrizeFormModal({ template, onSubmit, onCancel, loading, error }) {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (template) {
      const isText = template.value == null || Number(template.value) === 0;
      setForm({
        name: template.name || '',
        value: template.value != null ? String(template.value) : '',
        color: template.color || '#D4AF37',
        display_order: template.display_order != null ? String(template.display_order) : '0',
        is_active: template.is_active !== false,
        prize_type: isText ? 'text' : 'money',
      });
    } else {
      setForm(emptyForm);
    }
  }, [template]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = 'Nome obbligatorio';
    if (form.prize_type === 'money') {
      if (!form.value.trim() || isNaN(Number(form.value))) newErrors.value = 'Valore numerico obbligatorio';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    onSubmit({
      name: form.name.trim(),
      value: form.prize_type === 'money' ? Number(form.value) : 0,
      color: form.color || null,
      display_order: form.display_order ? Number(form.display_order) : 0,
      is_active: form.is_active,
    });
  };

  return (
    <form onSubmit={handleSubmit} style={styles.form}>
      {error && <div style={styles.error}>{error}</div>}

      <div>
        <label style={styles.fieldLabel}>Tipo premio</label>
        <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
          <label style={{ ...styles.checkboxLabel, flex: 1, padding: '10px 14px', borderRadius: 'var(--radius-md)', border: form.prize_type === 'money' ? '2px solid var(--color-primary)' : '2px solid rgba(212, 175, 55, 0.15)', backgroundColor: form.prize_type === 'money' ? 'rgba(212, 175, 55, 0.05)' : 'transparent', transition: 'all 0.15s' }}>
            <input type="radio" name="prize_type" value="money" checked={form.prize_type === 'money'} onChange={handleChange} style={{ accentColor: 'var(--color-primary)' }} />
            Denaro
          </label>
          <label style={{ ...styles.checkboxLabel, flex: 1, padding: '10px 14px', borderRadius: 'var(--radius-md)', border: form.prize_type === 'text' ? '2px solid var(--color-primary)' : '2px solid rgba(212, 175, 55, 0.15)', backgroundColor: form.prize_type === 'text' ? 'rgba(212, 175, 55, 0.05)' : 'transparent', transition: 'all 0.15s' }}>
            <input type="radio" name="prize_type" value="text" checked={form.prize_type === 'text'} onChange={handleChange} style={{ accentColor: 'var(--color-primary)' }} />
            Testo
          </label>
        </div>
      </div>

      <Input
        label={form.prize_type === 'text' ? 'Premio (testo)' : 'Nome'}
        name="name"
        value={form.name}
        onChange={handleChange}
        error={errors.name}
        placeholder={form.prize_type === 'text' ? 'es. Viaggio a Parigi' : 'es. 10.000'}
        required
      />

      {form.prize_type === 'money' && (
        <Input
          label="Valore (&euro;)"
          name="value"
          type="number"
          value={form.value}
          onChange={handleChange}
          error={errors.value}
          placeholder="10000"
          required
          min="0"
          step="0.01"
        />
      )}

      <div>
        <label style={styles.fieldLabel}>Colore</label>
        <div style={styles.colorField}>
          <input
            type="color"
            name="color"
            value={form.color || '#D4AF37'}
            onChange={handleChange}
            style={styles.colorInput}
          />
          <Input
            name="color"
            value={form.color}
            onChange={handleChange}
            placeholder="#D4AF37"
          />
          <div
            style={{
              ...styles.colorPreview,
              backgroundColor: form.color || 'transparent',
            }}
          />
        </div>
      </div>

      <Input
        label="Ordine"
        name="display_order"
        type="number"
        value={form.display_order}
        onChange={handleChange}
        placeholder="0"
        min="0"
      />

      <label style={styles.checkboxLabel}>
        <input
          type="checkbox"
          name="is_active"
          checked={form.is_active}
          onChange={handleChange}
          style={styles.checkbox}
        />
        Attivo
      </label>

      <div style={styles.formActions}>
        <Button variant="ghost" onClick={onCancel} type="button">
          <X size={16} />
          Annulla
        </Button>
        <Button variant="primary" type="submit" loading={loading}>
          <Save size={16} />
          {template ? 'Salva' : 'Salva'}
        </Button>
      </div>
    </form>
  );
}

export default function AdminPrizes() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [formError, setFormError] = useState(null);

  const loadTemplates = async () => {
    try {
      const result = await getPrizeTemplates();
      setTemplates(Array.isArray(result) ? result : result.templates || []);
    } catch {
      // handle silently
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  const handleCreate = async (data) => {
    setSaving(true);
    setFormError(null);
    try {
      await createPrizeTemplate(data);
      await loadTemplates();
      setShowForm(false);
    } catch (err) {
      setFormError(err.response?.data?.message || 'Errore durante la creazione del premio');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (data) => {
    if (!editTemplate) return;
    setSaving(true);
    setFormError(null);
    try {
      await updatePrizeTemplate(editTemplate.id, data);
      await loadTemplates();
      setEditTemplate(null);
    } catch (err) {
      setFormError(err.response?.data?.message || 'Errore durante la modifica del premio');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler rimuovere questo premio?')) return;
    try {
      await deletePrizeTemplate(id);
      await loadTemplates();
    } catch {
      // handle silently
    }
  };

  const openCreate = () => {
    setFormError(null);
    setShowForm(true);
  };

  const openEdit = (tpl) => {
    setFormError(null);
    setEditTemplate(tpl);
  };

  if (loading) {
    return (
      <div style={styles.centered}>
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Premi del gioco</h1>
          <p style={styles.subtitle}>Gestisci i premi disponibili per le partite</p>
        </div>
        <Button variant="primary" onClick={openCreate}>
          <Plus size={16} />
          Aggiungi premio
        </Button>
      </div>

      <div style={styles.tableWrapper}>
        {templates.length === 0 ? (
          <div style={styles.empty}>
            <p style={{ margin: 0 }}>Nessun premio creato. Clicca "Aggiungi premio" per iniziare.</p>
          </div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Nome</th>
                <th style={styles.th}>Valore</th>
                <th style={styles.th}>Colore</th>
                <th style={styles.th}>Ordine</th>
                <th style={styles.th}>Stato</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {templates.map((tpl) => (
                <tr
                  key={tpl.id}
                  style={styles.row}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-bg-card)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <td style={{ ...styles.td, color: 'var(--color-text-primary)', fontWeight: 500 }}>
                    {tpl.name}
                  </td>
                  <td style={{ ...styles.td, fontFamily: 'var(--font-mono)' }}>
                    {Number(tpl.value) > 0
                      ? `\u20AC ${Number(tpl.value).toLocaleString('it-IT', { minimumFractionDigits: 2 })}`
                      : <Badge variant="info">Testo</Badge>
                    }
                  </td>
                  <td style={styles.td}>
                    {tpl.color ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span
                          style={{
                            ...styles.colorSwatch,
                            backgroundColor: tpl.color,
                          }}
                        />
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem' }}>
                          {tpl.color}
                        </span>
                      </div>
                    ) : (
                      <span style={{ color: 'var(--color-text-muted)' }}>-</span>
                    )}
                  </td>
                  <td style={{ ...styles.td, fontFamily: 'var(--font-mono)' }}>
                    {tpl.display_order}
                  </td>
                  <td style={styles.td}>
                    <Badge variant={tpl.is_active ? 'success' : 'default'}>
                      {tpl.is_active ? 'Attivo' : 'Inattivo'}
                    </Badge>
                  </td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: 'var(--space-xs)', justifyContent: 'flex-end' }}>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(tpl)}>
                        <Edit2 size={14} />
                        Modifica
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(tpl.id)}>
                        <Trash2 size={14} />
                        Rimuovi
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title="Aggiungi premio"
      >
        <PrizeFormModal
          onSubmit={handleCreate}
          onCancel={() => setShowForm(false)}
          loading={saving}
          error={formError}
        />
      </Modal>

      <Modal
        isOpen={!!editTemplate}
        onClose={() => setEditTemplate(null)}
        title="Modifica premio"
      >
        <PrizeFormModal
          template={editTemplate}
          onSubmit={handleUpdate}
          onCancel={() => setEditTemplate(null)}
          loading={saving}
          error={formError}
        />
      </Modal>
    </div>
  );
}
